import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import WorkDetail from './WorkDetail';
import { useWorkDetailActions } from '../../shared/hooks/useApi/useWorkDetail';
import { useBulk } from '../../shared/hooks/useApi/useBulk';
import { useBulkStore } from '../../shared/store/WorkListStore';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { BULK_UPLOAD_IMAGE_MAX_COUNT } from '../../shared/constants/limits';
import { AlertPosition, AlertType, DetailType, ModalType } from '../../shared/enum/EnumRepository';
import { getImageDimensions, removeFileExtension } from '../../shared/util/imageUtils';
import createIcon from '../../asset/icon/create.png';
import reorderIcon from '../../asset/icon/reorder.png';
import confirmIcon from '../../asset/icon/confirm2.png';
import confirmIcon2 from '../../asset/icon/confirm3.png';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { useValidation } from '../../shared/hooks/useValidation';

const CREATE_DETAIL_INPUT_ID = 'works-create-detail-input';

const WorkDetailList: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { createDetailBulk } = useBulk();
  const { updateWorkDetailBulk, updateChangedWorkDetailBulkInfo } = useWorkDetailActions();
  const { setStandardModal } = useModalStore();
  const { setTotal, clearCurrent } = useBulkStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { isValidVideoUrl } = useValidation();
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const activeWorkDetailList = useWorkViewStore((s) => s.activeWorkDetailList);
  const hasChangedDetails = activeWorkDetailList.some((detail) => detail.hasChanged);

  if (activeWork === null) {
    return null;
  }

  const createSingleDetailFromFile = async (file: File) => {
    const results = await createDetailBulk([file], activeWork.id);
    if (!results?.length) return;
    const { width, height } = await getImageDimensions(file);
    await updateWorkDetailBulk({
      detailList: [{
        id: results[0].workDetailId,
        originUrl: results[0].originUrl,
        width,
        height,
        description: removeFileExtension(file.name),
      }],
    });
  };

  const handleAddWorkDetail = () => {
    document.getElementById(CREATE_DETAIL_INPUT_ID)?.click();
  };

  const handleCreateDetailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter((f) => f.type.startsWith('image/'));
    e.target.value = '';
    if (files.length === 0) return;

    if (files.length > BULK_UPLOAD_IMAGE_MAX_COUNT) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_UPLOAD_IMAGE_MAX_EXCEEDED', { maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT }),
      });
      return;
    }

    if (files.length === 1) {
      void createSingleDetailFromFile(files[0]);
      return;
    }

    setTotal(files.length);
    clearCurrent();
    setStandardModal({
      modalType: ModalType.BULK_UPLOAD,
      title: 'Detail Cuts',
      value: 'viewer',
      data: files,
      handleChange: () => {},
    });
  };

  const handleReorderDetailCuts = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: 'Detail Cut',
      value: null,
      handleChange: () => {},
    });
  };

  const handleUpdateChanged = async () => {
    const invalidVideo = activeWorkDetailList.find((detail) =>
      detail.hasChanged
      && (detail.detailType ?? DetailType.IMAGE) === DetailType.VIDEO
      && !!detail.videoUrl
      && !isValidVideoUrl(detail.videoUrl),
    );
    if (invalidVideo) {
      document.getElementById(`work-detail-${invalidVideo.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }
    await updateChangedWorkDetailBulkInfo();
  };

  return (
    <>
      {(isEditMode || activeWorkDetailList.length > 0) &&
        <TitleRow>
          {isEditMode &&
            <BtnWrapper data-has-changed={hasChangedDetails ? 'true' : 'false'}>
              <HiddenFileInput
                id={CREATE_DETAIL_INPUT_ID}
                type="file"
                accept="image/*"
                multiple
                onChange={handleCreateDetailFileChange}
              />
              {hasChangedDetails && (
                <HintWrapper content={getHintMessage('UPDATE_DETAIL_CUTS')} toLeft={true}>
                  <UniversalIconBtn
                    isPrimary={true}
                    size="fit"
                    border={true}
                    backgroundImg={confirmIcon}
                    backgroundImgHover={confirmIcon2}
                    onClick={handleUpdateChanged}
                  />
                </HintWrapper>
              )}
              <HintWrapper content={getHintMessage('ADD_DETAIL_CUT_WORK')} toLeft={true}>
                <UniversalIconBtn 
                  size="fit" 
                  border={true} 
                  backgroundImg={createIcon} 
                  onClick={handleAddWorkDetail} />
              </HintWrapper>
              {activeWorkDetailList.length > 1 && (
                <HintWrapper content={getHintMessage('REORDER_DETAIL_CUTS')} toLeft={true}>
                  <UniversalIconBtn 
                    size="fit" 
                    border={true} 
                    backgroundImg={reorderIcon} 
                    onClick={handleReorderDetailCuts} />
                </HintWrapper>
              )}
            </BtnWrapper>
          }
        </TitleRow>
      }
      {activeWorkDetailList.length > 0 &&
        <WorkDetailContainer>
          {activeWorkDetailList.map((wd, index) => (
            <WorkDetail key={wd.id} index={index + 1} workId={activeWork.id} data={wd} priority={index < 1} />
          ))}
        </WorkDetailContainer>
      }
    </>
  );
}

const TitleRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 1px;
  margin-bottom: 6px;
  gap: 0.5vw;
`;

const WorkDetailContainer = styled.div`
  position: relative;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const BtnWrapper = styled.div`
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin-left: auto;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

export default WorkDetailList;
